import React, { type FC } from 'react'
import {
  Box,
  Modal,
  Pill,
  ProgressIndicator,
  StepStatus,
} from '@moonpig/launchpad-components'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { useExperimentBoolean } from '@moonpig/web-core-eppo/browser'
import { useIsMobile } from '@moonpig/web-shared-utils'
import { ProductInfoWithQuery } from '../ProductInfoWithQuery'
import { createModalSizeChangeGAEventDetails } from '../../analytics'
import {
  AddedToBasketEvent,
  ProductContext,
  ProductInfoProduct,
  UpsellProduct,
} from '../../types'
import { isCardProduct } from '../../utils/isCardProduct'
import { transformPill } from '../../utils/transformPill'
import { ProductStoreProvider } from '../../contexts/product'
import { useProductInitialVariantSelectionState } from '../../utils/useProductInitialVariantSelectionState'
import { ProductTrackingProvider } from '../../contexts/tracking'
import { OnConfigureModulesCallback } from '../../modules/types'
import { ModuleLayoutContainer } from '../../modules/LayoutContainer/ModuleLayoutContainer'
import { ScrollToTop } from './ScrollToTop'
import { isDigitalGiftCard } from '../../utils/isDigitalGift/isDigitalGift'
import { hasMultipleVariants } from '../../utils/hasMultipleVariants'
import { StepHeading } from '../StepHeading'

type Props = {
  index?: number
  product: ProductInfoProduct
  upsellProduct?: UpsellProduct
  groupCardProject?: string
  triggerElementRef?: React.RefObject<HTMLAnchorElement | null>
  configuration: {
    showProductTabs: boolean
    showStickyCta: boolean
  }
  onConfigureModules?: OnConfigureModulesCallback
  productContext: ProductContext
  isOpen: boolean
  onAddToBasket: (event: AddedToBasketEvent) => void
  onDismiss: () => void
}

export const ProductModal: FC<Props> = ({
  index,
  product,
  upsellProduct,
  groupCardProject,
  triggerElementRef,
  configuration,
  productContext,
  isOpen,
  onAddToBasket,
  onDismiss,
  onConfigureModules,
}) => {
  const isMobile = useIsMobile()

  const renderCustomLabelFn = isMobile
    ? () => <StepHeading activeStep={1} totalSteps={3} />
    : () => <Box height={6} />

  const { variantKey } = useProductInitialVariantSelectionState()

  const isCard = product.category && isCardProduct(product.category.department)
  const isTshirt = product.category?.id === 12

  const isGiftCardVariantsEnabled = useExperimentBoolean('gift-card-variants', {
    fallback: false,
  })

  const hideInitialVariantSelection =
    isGiftCardVariantsEnabled &&
    isDigitalGiftCard(product.category.department) &&
    hasMultipleVariants(product.variants)

  const pill = product.primaryProductPill
    ? transformPill(product.primaryProductPill)
    : undefined

  const isFrictionlessFlowEnabled = useExperimentBoolean(
    'frictionless-flow-v1',
    {
      fallback: false,
    },
  )

  const banner = (
    <Box flexGrow={2} alignSelf="center">
      {!isFrictionlessFlowEnabled && isCard && !!pill && (
        <Box ml={6}>
          <Box mr={{ xs: 4, md: 6 }}>
            <Pill
              data-testid="card-banner-pill"
              label={pill.displayLabel}
              variant={pill.displayVariant}
            />
          </Box>
        </Box>
      )}
      {isFrictionlessFlowEnabled && isCard && (
        <Box m={6} mr={{ xs: 4, md: 6 }} mt={10}>
          <ProgressIndicator
            minimal
            steps={[
              {
                status: StepStatus.active,
                progressPercentage: 50,
                renderLabel: renderCustomLabelFn,
              },
              {
                status: StepStatus.incomplete,
              },
              {
                status: StepStatus.incomplete,
              },
              {
                status: StepStatus.incomplete,
              },
            ]}
          />
        </Box>
      )}
    </Box>
  )

  return (
    <Modal
      label={product?.title}
      isOpen={isOpen}
      hasCloseButton
      closeButtonLabel="Close"
      onDismiss={onDismiss}
      testId="quick-view"
      bannerContent={banner}
      // eslint-disable-next-line no-warning-comments
      // TODO: Launchpad need to support nullable triggerRef for R19
      triggerRef={triggerElementRef as React.RefObject<HTMLAnchorElement>}
    >
      <ProductStoreProvider
        initialProduct={product}
        initialVariantKey={variantKey}
        upsellProduct={upsellProduct}
        productContext={productContext}
        hideInitialVariantSelection={hideInitialVariantSelection}
      >
        <ProductTrackingProvider
          pageLocation="product details"
          pageComponent="popup"
        >
          <ScrollToTop>
            <ModuleLayoutContainer
              onConfigureModules={onConfigureModules}
              context={productContext}
            >
              {/* This child only renders if there's no config for the particular modal */}
              <ProductInfoWithQuery
                product={product}
                productIndex={index}
                onAddToBasket={e =>
                  onAddToBasket({ ...e, index: e.productIndex })
                }
                onVariantChange={(variantValue: string) => {
                  trackGAEvent(
                    createModalSizeChangeGAEventDetails(variantValue),
                  )
                }}
                showTitle={!isCard && !isTshirt}
                showMoreInfo
                showProductTabs={configuration.showProductTabs}
                hideStickyCta={!configuration.showStickyCta}
                pageLocation={productContext.location}
                groupCardProject={groupCardProject}
                isFromModal
                pageType={productContext.pageType}
              />
            </ModuleLayoutContainer>
          </ScrollToTop>
        </ProductTrackingProvider>
      </ProductStoreProvider>
    </Modal>
  )
}
